/**
 * LabDetailPanel.tsx
 *
 * Renders a Quest/Vibrant/Access result or a Vibrant outbound order detail inline in
 * the patient-detail right sidebar, mirroring the note detail-panel pattern
 * (compact top bar with Expand -> full modal + Close). The detail content is
 * the same shared body used by the modal shells, so the sidebar and the
 * expanded modal always stay in sync.
 */
import { Maximize2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { LabPreviewTarget } from './LabPdfPreviewPanel';
import { QuestLabDetailBody } from './QuestLabDetailModal';
import { VibrantLabDetailBody } from './VibrantLabDetailModal';
import { AccessLabDetailBody } from './AccessLabDetailModal';

/** The subset of preview targets this panel knows how to render. */
export type LabDetailTarget = Extract<
  LabPreviewTarget,
  { kind: 'quest-detail' } | { kind: 'vibrant-detail' } | { kind: 'vibrant-order-detail' } | { kind: 'access-detail' }
>;

interface LabDetailPanelProps {
  target: LabDetailTarget;
  onExpand: () => void;
  onClose: () => void;
}

export function LabDetailPanel({ target, onExpand, onClose }: LabDetailPanelProps) {
  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900" title={target.title}>
          {target.title}
        </h3>
        <div className="flex shrink-0 items-center gap-1">
          <Button size="sm" variant="secondary" onClick={onExpand}>
            <Maximize2 className="mr-1 h-3.5 w-3.5" />
            Expand
          </Button>
          <Button size="sm" variant="secondary" onClick={onClose}>
            <X className="mr-1 h-3.5 w-3.5" />
            Close
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1">
        {target.kind === 'quest-detail' ? (
          <QuestLabDetailBody labResultId={target.labResultId} orderId={target.orderId} />
        ) : target.kind === 'vibrant-detail' ? (
          <VibrantLabDetailBody source="vibrant" id={target.labResultId} />
        ) : target.kind === 'access-detail' ? (
          <AccessLabDetailBody id={target.labResultId} />
        ) : (
          <VibrantLabDetailBody source="vibrant-order" id={target.orderId} />
        )}
      </div>
    </div>
  );
}
